import Phaser from 'phaser';
import { ShapeFactory, ShapeType, ShapeObject } from '../entities/ShapeFactory';
import { GAME, GAMEPLAY } from '../config/gameConfig';

/**
 * Spawn weights for each shape type (controls probability)
 */
const SPAWN_WEIGHTS: Record<ShapeType, number> = {
  [ShapeType.SQUARE]: 0.5,    // 50% - most stable, best for foundation
  [ShapeType.CIRCLE]: 0.3,    // 30% - medium difficulty
  [ShapeType.TRIANGLE]: 0.2,  // 20% - challenging
};

/**
 * SpawnManager - Handles creation and spawning of game pieces
 */
export class SpawnManager {
  private shapeFactory: ShapeFactory;
  private spawnX: number;
  private spawnY: number;

  constructor(scene: Phaser.Scene) {
    this.shapeFactory = new ShapeFactory(scene);
    this.spawnX = GAME.WIDTH / 2;
    this.spawnY = GAMEPLAY.SPAWN_Y;
  }

  /**
   * Spawn a random shape at the spawn point
   */
  spawnRandom(): ShapeObject {
    const type = this.getWeightedRandomType();
    return this.spawn(type);
  }

  /**
   * Spawn a specific shape type at the spawn point
   */
  spawn(type: ShapeType): ShapeObject {
    return this.shapeFactory.createShape(type, this.spawnX, this.spawnY);
  }

  /**
   * Spawn a shape at a specific position
   */
  spawnAt(type: ShapeType, x: number, y: number): ShapeObject {
    return this.shapeFactory.createShape(type, x, y);
  }

  /**
   * Get a random shape type based on spawn weights
   */
  private getWeightedRandomType(): ShapeType {
    const types = Object.keys(SPAWN_WEIGHTS) as ShapeType[];
    const weights = types.map(t => SPAWN_WEIGHTS[t]);
    return this.weightedRandom(types, weights);
  }

  /**
   * Weighted random selection from an array
   */
  private weightedRandom<T>(items: T[], weights: number[]): T {
    const total = weights.reduce((sum, w) => sum + w, 0);
    let random = Math.random() * total;

    for (let i = 0; i < items.length; i++) {
      random -= weights[i];
      if (random <= 0) {
        return items[i];
      }
    }

    return items[items.length - 1];
  }

  /**
   * Set the spawn position
   */
  setSpawnPosition(x: number, y: number): void {
    this.spawnX = x;
    this.spawnY = y;
  }

  /**
   * Get the current spawn X position
   */
  getSpawnX(): number {
    return this.spawnX;
  }

  /**
   * Get the current spawn Y position
   */
  getSpawnY(): number {
    return this.spawnY;
  }
}
